import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { getDealerBasePath } from "../../navigation/getDealerBasePath";
import { useCreateComparisonLead } from "../../queries/useCreateComparisonLead";
import { useAuth } from "../../stores/hooks/useAuth";
import { useDealerAccountInfo } from "../../stores/hooks/useDealerAccountInfo";
import { encodeTokenToHash } from "../../util/encodeTokenToHash";
import { getPdfDownloadLink } from "../../util/getPdfDownloadLink";
import { useTireResults } from "../Tires/Results/useTireResults";
import { LeadFormButtons } from "./LeadFormButtons";
import {
  LeadFormData,
  LeadFormModal,
  useLeadFormModalState,
} from "./LeadFormModal";

type LeadFormCompareProps = {
  compareIds: string[];
};

export const compareActionPrint = "#print";
export const compareActionEmail = "#email";
export const compareActionDownload = "#download";

export const LeadFormCompare: FunctionComponent<LeadFormCompareProps> = ({
  compareIds,
}) => {
  const { t } = useTranslation();
  const { dealerName } = useDealerAccountInfo();
  const createComparisonLead = useCreateComparisonLead();
  const { data: results } = useTireResults();
  const [token] = useAuth();

  const downloadState = useLeadFormModalState(compareActionDownload);
  const emailState = useLeadFormModalState(compareActionEmail);
  const printState = useLeadFormModalState(compareActionPrint);

  const printPagePath = `${getDealerBasePath()}/product/tires/compare/${compareIds.join(
    ","
  )}/vehicle/${results?.resultParameters?.vehicleId || "null"}`;

  const downloadRedirectActon = (formData: LeadFormData): void => {
    const link = getPdfDownloadLink({
      ...formData,
      filename: `Tire Comparison - ${dealerName}.pdf`,
      orientation: "landscape",
      path: `${printPagePath}${compareActionPrint}`,
      sso: encodeTokenToHash(token),
    });
    window.location.assign(link);
  };

  const printRedirectActon = (formData: LeadFormData): void => {
    const queryString = new URLSearchParams(formData).toString();
    window.open(`${printPagePath}?${queryString}${compareActionPrint}`);
  };

  return (
    <>
      <LeadFormButtons />
      <LeadFormModal
        generatedByPrefix="compare-"
        hook={emailState}
        redirectAction={null}
        submitAction={createComparisonLead}
        subtitle={t("leadForm.compare.subtitle.email")}
        title={t("leadForm.compare.title.email")}
        type="email"
      />
      <LeadFormModal
        generatedByPrefix="compare-"
        hook={downloadState}
        redirectAction={downloadRedirectActon}
        submitAction={createComparisonLead}
        subtitle={t("leadForm.compare.subtitle.download")}
        title={t("leadForm.compare.title.download")}
        type="download"
      />
      <LeadFormModal
        generatedByPrefix="compare-"
        hook={printState}
        redirectAction={printRedirectActon}
        submitAction={createComparisonLead}
        subtitle={t("leadForm.compare.subtitle.print")}
        title={t("leadForm.compare.title.print")}
        type="print"
      />
    </>
  );
};
